import { divide } from "@aprao/common/src/math/divide.js";
import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import {
  formatDecimalNumber,
  formatPercentage,
} from "@aprao/common/src/numberFormats.js";
import { capitalize } from "@aprao/common/src/string/capitalize.js";
import type { AppraisalDocumentFacade } from "@aprao/model/src/stores/AppraisalDocumentFacade.js";
import type { FinanceFacade } from "@aprao/model/src/stores/FinanceFacade.js";
import type { FlowFacade } from "@aprao/model/src/stores/FlowFacade.js";
import type { AnyLenderFacade } from "@aprao/model/src/stores/lender/AnyLenderFacade.js";
import { interestLenderPredicate } from "@aprao/model/src/stores/lender/interestLenderPredicate.js";
import { readOnlyRelativesSetFacadeWithAbsolute } from "@aprao/model/src/stores/RelativesSetFacade.js";
import type { ResidualFacade } from "@aprao/model/src/stores/ResidualFacade.js";
import { mapForNullable } from "option-t/nullable";

import { useI18n } from "../../common/ApraoI18n.js";
import { FinanceSummaryRelatives } from "../../common/FinanceSummaryRelatives.js";
import { useNumberUnits } from "../../common/useNumberUnits.js";
import { buildLenderName } from "../../i18n/buildLenderName.js";
import { labelByInterestPayoutTypeLabels } from "../../i18n/interestPayoutTypeLabels.js";
import { labelByInterestRatePeriod } from "../../i18n/labelByInterestRatePeriod.js";
import { Section } from "../Section.js";
import { SimpleTableRow, SimpleTableSection } from "../SimpleTableRow.js";

const createAvailableBalanceRelatives = (params: {
  appraisal: AppraisalDocumentFacade;
  lender: AnyLenderFacade;
  residual: ResidualFacade;
  finance: FinanceFacade;
  flow: FlowFacade;
}) => {
  const { appraisal, lender, residual, finance, flow } = params;

  return readOnlyRelativesSetFacadeWithAbsolute(
    {
      relatives: {
        absolute:
          finance.getAvailableBalanceByLender(lender)?.toFraction() ?? null,
        ratioDebtFundedDevCost: null,
        ratioDebtFundedDevAndFinanceCost: null,
        ratioDebtFundedGdv: null,
      },
    },
    {
      ratioDebtFundedDevCost: () =>
        residual.getDevelopmentCostsTotal("debtFunded"),
      ratioDebtFundedDevAndFinanceCost: () =>
        flow.getDevelopmentAndFinanceCostsTotal("debtFunded"),
      ratioDebtFundedGdv: () =>
        appraisal.stages.revenue.getGrossDevelopmentValue("debtFunded"),
    },
  );
};

const BreakdownSection = (props: {
  appraisal: AppraisalDocumentFacade;
  residual: ResidualFacade;
  finance: FinanceFacade;
  flow: FlowFacade;
}) => {
  const { appraisal, residual, finance, flow } = props;

  const { developerContribution } = new FinanceSummaryRelatives({
    appraisal: appraisal,
    residual: residual,
    finance: finance,
    flow: flow,
  });

  const { msg } = useI18n();
  const { currency } = useNumberUnits();

  return (
    <SimpleTableSection>
      <SimpleTableRow
        isHeader
        cells={[
          "Finance summary breakdown",
          null,
          "% Cost (inc. finance)",
          "% Cost",
          msg("global.percentGdv"),
          `Total (${currency.symbol})`,
        ]}
      />
      {residual.lenders.map((lender, idxLender) => {
        const totalFacility =
          lender.facilitySize.relativesSet.getRelative("absolute");

        return (
          <SimpleTableRow
            key={idxLender}
            cells={[
              buildLenderName(
                lender.$model,
                appraisal.$model.stages.lending.lenders,
              ),
              null,
              formatPercentage(
                divide(
                  totalFacility,
                  flow.getDevelopmentAndFinanceCostsTotal("debtFunded"),
                ) ?? FRACTION_ZERO,
              ),
              formatPercentage(
                lender.facilitySize.relativesSet.getRelative(
                  "ratioDebtFundedDevCost",
                ) ?? FRACTION_ZERO,
              ),
              formatPercentage(
                lender.facilitySize.relativesSet.getRelative(
                  "ratioDebtFundedGdv",
                ) ?? FRACTION_ZERO,
              ),
              formatDecimalNumber(totalFacility ?? FRACTION_ZERO),
            ]}
          />
        );
      })}
      <SimpleTableRow
        cells={[
          "Developer's equity",
          null,
          formatPercentage(
            developerContribution.relativesSet.getRelative(
              "ratioDebtFundedDevAndFinanceCost",
            ) ?? FRACTION_ZERO,
          ),
          formatPercentage(
            developerContribution.relativesSet.getRelative(
              "ratioDebtFundedDevCost",
            ) ?? FRACTION_ZERO,
          ),
          formatPercentage(
            developerContribution.relativesSet.getRelative(
              "ratioDebtFundedGdv",
            ) ?? FRACTION_ZERO,
          ),
          formatDecimalNumber(
            developerContribution.relativesSet.getRelative("absolute") ??
              FRACTION_ZERO,
          ),
        ]}
      />
    </SimpleTableSection>
  );
};

const LenderSection = (props: {
  appraisal: AppraisalDocumentFacade;
  lender: AnyLenderFacade;
  residual: ResidualFacade;
  finance: FinanceFacade;
  flow: FlowFacade;
}) => {
  const { lender, appraisal, residual, finance, flow } = props;

  const availableBalanceRelatives = createAvailableBalanceRelatives({
    appraisal,
    lender,
    residual,
    finance,
    flow,
  });

  const { msg } = useI18n();
  const { currency } = useNumberUnits();

  return (
    <SimpleTableSection>
      <SimpleTableRow
        isHeader
        cells={[
          lender.name,
          null,
          null,
          "% Cost",
          msg("global.percentGdv"),
          `Total (${currency.symbol})`,
        ]}
      />
      <SimpleTableRow
        cells={[
          "Total facility",
          null,
          null,
          formatPercentage(
            lender.facilitySize.relativesSet.getRelative(
              "ratioDebtFundedDevCost",
            ) ?? FRACTION_ZERO,
          ),
          formatPercentage(
            lender.facilitySize.relativesSet.getRelative(
              "ratioDebtFundedGdv",
            ) ?? FRACTION_ZERO,
          ),
          formatDecimalNumber(
            lender.facilitySize.relativesSet.getRelative("absolute") ??
              FRACTION_ZERO,
          ),
        ]}
      />
      {interestLenderPredicate(lender) && (
        <SimpleTableRow
          cellCount={3}
          cells={[
            `${capitalize(
              labelByInterestPayoutTypeLabels.get(lender.interest.payoutType),
            )} interest at ${formatPercentage(
              lender.interest.rate.rateMargin ?? FRACTION_ZERO,
            )} ${labelByInterestRatePeriod[lender.interest.rate.ratePeriod]}`,
            null,
            formatDecimalNumber(
              flow.getInterestAmountByLender(lender) ?? FRACTION_ZERO,
            ),
          ]}
        />
      )}
      {lender.loanType === "profitShare" && (
        <SimpleTableRow
          cellCount={3}
          cells={[
            `Share of profit ${formatPercentage(
              lender.profitShare ?? FRACTION_ZERO,
              true,
            )}`,
            null,
            formatDecimalNumber(
              flow.getProfitShareAmountByLender(lender) ?? FRACTION_ZERO,
            ),
          ]}
        />
      )}
      <SimpleTableRow
        bold
        borderTop
        borderBottom
        cells={[
          "Available balance",
          null,
          null,
          formatPercentage(
            availableBalanceRelatives.relativesSet.getRelative(
              "ratioDebtFundedDevCost",
            ) ?? FRACTION_ZERO,
          ),
          formatPercentage(
            availableBalanceRelatives.relativesSet.getRelative(
              "ratioDebtFundedGdv",
            ) ?? FRACTION_ZERO,
          ),
          formatDecimalNumber(
            availableBalanceRelatives.relativesSet.getRelative("absolute") ??
              FRACTION_ZERO,
          ),
        ]}
      />
      <SimpleTableRow
        borderBottom
        cells={[
          "Fees",
          null,
          null,
          "% total facility",
          msg("global.percentGdv"),
          `Total (${currency.symbol})`,
        ]}
      />
      {lender.fees.map((fee, feeIdx) => {
        const feeRelativesDelegate =
          finance.getLenderFeeRelativesDelegateByStore(fee, lender);

        return (
          <SimpleTableRow
            key={feeIdx}
            cells={[
              fee.named.name,
              null,
              null,
              formatPercentage(
                feeRelativesDelegate.getRelative("ratioFacilitySize") ??
                  FRACTION_ZERO,
              ),
              formatPercentage(
                feeRelativesDelegate.getRelative("ratioDebtFundedGdv") ??
                  FRACTION_ZERO,
              ),
              formatDecimalNumber(
                feeRelativesDelegate.getRelative("absolute") ?? FRACTION_ZERO,
              ),
            ]}
          />
        );
      })}
      <SimpleTableRow
        borderBottom
        borderTop
        bold
        cells={[
          "Total fees",
          null,
          null,
          null,
          null,
          formatDecimalNumber(
            finance.getLenderFeesTotalByLender(lender) ?? FRACTION_ZERO,
          ),
        ]}
      />
    </SimpleTableSection>
  );
};

const EquitySection = (props: { finance: FinanceFacade; flow: FlowFacade }) => {
  const { finance, flow } = props;

  const { msg } = useI18n();
  const { currency } = useNumberUnits();

  return (
    <SimpleTableSection>
      <SimpleTableRow
        isHeader
        cells={[
          "Developer's equity",
          null,
          null,
          "RoE",
          msg("global.equityIrr"),
          `Total (${currency.symbol})`,
        ]}
      />
      <SimpleTableRow
        cells={[
          "Total equity",
          null,
          null,
          formatPercentage(flow.returnOnDeveloperContribution ?? FRACTION_ZERO),
          mapForNullable(flow.developerContributionIrr, formatPercentage) ??
            "N/A",
          formatDecimalNumber(finance.developerContribution ?? FRACTION_ZERO),
        ]}
      />
    </SimpleTableSection>
  );
};

export const Finance = (props: {
  appraisal: AppraisalDocumentFacade;
  residual: ResidualFacade;
  finance: FinanceFacade;
  flow: FlowFacade;
}) => {
  const { appraisal, residual, finance, flow } = props;

  const { msg } = useI18n();

  return (
    <Section title={msg("global.finance")}>
      <BreakdownSection
        appraisal={appraisal}
        residual={residual}
        finance={finance}
        flow={flow}
      />

      {residual.lenders.map((lender, idx) => (
        <LenderSection
          key={idx}
          appraisal={appraisal}
          lender={lender}
          residual={residual}
          finance={finance}
          flow={flow}
        />
      ))}

      <EquitySection finance={finance} flow={flow} />
    </Section>
  );
};
